import type { GenericSchema } from "valibot";
import { getDotPath, safeParse } from "valibot";
import { createEffect, createSignal, createUniqueId, getOwner, on, runWithOwner, untrack } from "solid-js";
import { createStore } from "solid-js/store";
import { cloneDeep, isEqual, isNil } from "lodash-es";
import { createTimer } from "@solid-primitives/timer";
import { SLOW_SUBMIT_DELAY } from "../../constants";
import { trackStore } from "@solid-primitives/deep";
import logger from "../../utils/logger";

/** Either a property of the form data, or the dot path to a value nested inside one ("items.0.name"). */
export type FormPath<T> = keyof T | `${Extract<keyof T, string>}.${string}`;

export type GetValueFn<T> = <K extends keyof T>(property: K) => T[K] | undefined;
export type GetErrorFn<T> = (property: FormPath<T>) => string | undefined;
export type UpdateValueFn<T> = <K extends keyof T>(property: keyof T, value: T[K] | undefined | null) => void;
export type UpdateErrorFn<T> = (property: FormPath<T>, value: string) => void;

export type FormApi<T> = {
  id: string;
  submit: () => Promise<void>;
  reset: (value?: Partial<T>) => void;
  isSubmitting: () => boolean;
  isSubmittingSlow: () => boolean;
  submitDisabled: () => boolean;
  hasUnsavedChanges: () => boolean;
  getValue: GetValueFn<T>;
  getError: GetErrorFn<T>;
  updateValue: UpdateValueFn<T>;
  updateError: UpdateErrorFn<T>;
  setInitialValue: UpdateValueFn<T>;
};

export const createForm = <T extends Record<string, unknown>>(
  schema: GenericSchema,
  onSubmit: ((value: T) => Promise<void>) | ((value: T) => void),
  initialValue?: Partial<T>,
): FormApi<T> => {
  const owner = getOwner();
  const formId = createUniqueId();
  const [formData, setFormData] = createStore<{ data: Partial<T> }>({ data: cloneDeep(initialValue) ?? {} });
  const [initialData, setInitialData] = createSignal<Partial<T>>(cloneDeep(initialValue) ?? {});
  const [errors, setErrors] = createSignal<Partial<Record<FormPath<T>, string>>>({});
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [isSubmittingSlow, setIsSubmittingSlow] = createSignal(false);
  createTimer(
    () => {
      setIsSubmittingSlow(true);
    },
    () => isSubmitting() && SLOW_SUBMIT_DELAY,
    setTimeout,
  );

  const validate = () => {
    const { success, issues, output } = safeParse(
      schema,
      untrack(() => formData.data),
    );

    const errors =
      issues?.reduce((acc: Partial<Record<FormPath<T>, string>>, cur) => {
        // Key on the full dot path so nested schemas can report an error per field instead of one per property.
        const firstPath = cur.path?.[0];
        const path =
          (getDotPath(cur) as FormPath<T> | null) ??
          (!isNil(firstPath) && "key" in firstPath ? (firstPath.key as FormPath<T> | undefined) : undefined);
        if (!isNil(path)) {
          acc[path] = cur.message;
        }
        return acc;
      }, {}) ?? {};

    setErrors(errors);

    return { success, output };
  };

  const revalidateError = (property: FormPath<T>) => {
    // Only refresh an error that is already shown, so untouched fields don't light up while the user is typing.
    if (isNil(untrack(errors)[property])) {
      return;
    }
    const issue = safeParse(
      schema,
      untrack(() => formData.data),
    ).issues?.find((issue) => getDotPath(issue) === property);
    setErrors((prev) => ({ ...prev, [property]: issue?.message }));
  };

  const [submitDisabled, setSubmitDisabled] = createSignal(true);
  createEffect(
    on(
      () => trackStore(formData.data),
      (data) => {
        const hasNonEmptyErrors =
          safeParse(schema, data).issues?.some(
            (issue) =>
              issue.type === "non_empty" ||
              issue.type === "non_nullish" ||
              issue.type === "non_optional" ||
              issue.type === "check",
          ) ?? false;
        setSubmitDisabled(hasNonEmptyErrors);
      },
    ),
  );

  const submit = async () => {
    try {
      setIsSubmitting(true);
      const submit = untrack(() => onSubmit);
      const { success, output } = validate();

      if (success) {
        await runWithOwner(owner, async () => {
          await submit(output as T);
        });
      }
    } catch (e) {
      logger.warn("Failed to submit data", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = (value?: Partial<T>) => {
    const nextData = cloneDeep(value) ?? cloneDeep(initialValue) ?? {};
    setFormData({ data: nextData });
    setInitialData(cloneDeep(nextData));
    setErrors({});
  };

  const hasUnsavedChanges = () => !isEqual(trackStore(formData.data), initialData());

  return {
    id: formId,
    submit,
    reset,
    isSubmitting,
    isSubmittingSlow,
    submitDisabled,
    hasUnsavedChanges,
    getValue: (field) => formData.data[field],
    getError: (field) => errors()[field],
    updateValue: (property, value) => {
      setFormData("data", (data) => ({
        ...data,
        [property]: value,
      }));
      revalidateError(property);
    },
    setInitialValue: (property, value) =>
      setInitialData((data) => ({
        ...data,
        [property]: cloneDeep(value),
      })),
    updateError: (field, message) => {
      setErrors((prev) => ({ ...prev, [field]: message }));
    },
  };
};
